import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { MapPin, Calendar } from 'lucide-react'
import { Experience as ExperienceType } from '../types'
import { fadeUp, staggerContainer, viewportOnce } from '../lib/motion'

interface ExperienceProps {
  experiences: ExperienceType[]
}

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
}

const parseMonthYear = (raw: string): Date | null => {
  const value = raw.trim()
  if (/present|now|current/i.test(value)) return new Date()
  const match = value.match(/([A-Za-z]+)\.?\s+(\d{4})/)
  if (!match) return null
  const month = MONTHS[match[1].slice(0, 3).toLowerCase()]
  if (month === undefined) return null
  return new Date(parseInt(match[2], 10), month, 1)
}

// "Aug 2025 - Present" -> "11 mos" / "1 yr 2 mos" (LinkedIn-style, inclusive)
const formatDuration = (duration: string): string => {
  const parts = duration.split(/\s*[-–—]\s*/)
  if (parts.length < 2) return ''
  const start = parseMonthYear(parts[0])
  const end = parseMonthYear(parts[1])
  if (!start || !end) return ''
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
  if (months < 1) months = 1
  const years = Math.floor(months / 12)
  const rest = months % 12
  const chunks: string[] = []
  if (years > 0) chunks.push(`${years} yr${years > 1 ? 's' : ''}`)
  if (rest > 0) chunks.push(`${rest} mo${rest > 1 ? 's' : ''}`)
  return chunks.join(' ')
}

const getInitials = (company: string): string => {
  const paren = company.match(/\(([^)]+)\)/)
  if (paren) return paren[1].slice(0, 3).toUpperCase()
  const words = company.replace(/[^A-Za-z\s]/g, ' ').split(/\s+/).filter((w) => w.length > 1)
  if (words.length === 0) return company.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

// Map company names to domains for Clearbit logo lookup.
const COMPANY_DOMAINS: { test: RegExp; domain: string }[] = [
  { test: /bni|bank negara/i, domain: 'bni.co.id' },
  { test: /vobis/i, domain: 'vobis.io' },
  { test: /synapsis/i, domain: 'synapsis.id' },
  { test: /ruangguru/i, domain: 'ruangguru.com' },
  { test: /brawijaya|filkom|computer science|doctoral/i, domain: 'ub.ac.id' },
]

const getCompanyDomain = (company: string): string | null =>
  COMPANY_DOMAINS.find((c) => c.test.test(company))?.domain ?? null

// Company logo via Clearbit, falling back to an initials avatar on error / no domain.
const CompanyLogo: React.FC<{ company: string; isBNI: boolean }> = ({ company, isBNI }) => {
  const domain = getCompanyDomain(company)
  const [failed, setFailed] = useState(false)

  if (domain && !failed) {
    return (
      <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white border border-gray-200 dark:border-gray-600 shadow-md flex items-center justify-center overflow-hidden">
        <img
          src={`https://logo.clearbit.com/${domain}`}
          alt={`${company} logo`}
          loading="lazy"
          className="w-full h-full object-contain p-1.5"
          onError={() => setFailed(true)}
        />
      </div>
    )
  }

  return (
    <div
      className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-md bg-gradient-to-br ${
        isBNI ? 'from-accent-500 to-accent-700' : 'from-primary-500 to-primary-700'
      }`}
    >
      {getInitials(company)}
    </div>
  )
}

const getTypeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case 'full-time':
      return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300'
    case 'contract':
      return 'bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-300'
    case 'part-time':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
    case 'freelance':
      return 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'
    case 'volunteer':
      return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
}

interface CompanyGroup {
  company: string
  location: string
  roles: ExperienceType[]
}

// Group consecutive-by-company entries (first appearance wins) — LinkedIn style.
const groupByCompany = (experiences: ExperienceType[]): CompanyGroup[] => {
  const groups: CompanyGroup[] = []
  const index = new Map<string, CompanyGroup>()
  for (const exp of experiences) {
    let group = index.get(exp.company)
    if (!group) {
      group = { company: exp.company, location: exp.location, roles: [] }
      index.set(exp.company, group)
      groups.push(group)
    }
    group.roles.push(exp)
  }
  return groups
}

const Experience: React.FC<ExperienceProps> = ({ experiences }) => {
  const groups = groupByCompany(experiences)

  const startYears = experiences
    .map((e) => parseMonthYear(e.duration.split(/\s*[-–—]\s*/)[0]))
    .filter((d): d is Date => d !== null)
    .map((d) => d.getFullYear())
  const careerYears = startYears.length ? new Date().getFullYear() - Math.min(...startYears) : 0
  const companyCount = new Set(experiences.map((e) => e.company)).size

  return (
    <section id="experience" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Work Experience
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            From teaching first-year students to shipping production backends — and now running
            production systems at one of Indonesia's largest banks.
          </p>
        </motion.div>

        {/* LinkedIn-style vertical timeline */}
        <motion.div
          className="max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {groups.map((group, gIndex) => {
            const isBNI = /\bBNI\b|bank negara/i.test(group.company)
            return (
              <motion.div key={gIndex} className="card p-5 sm:p-6" variants={fadeUp} whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                {/* Company header */}
                <div className="flex items-center gap-4 mb-4">
                  <CompanyLogo company={group.company} isBNI={isBNI} />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-snug">
                      {group.company}
                    </h3>
                    {group.roles.length > 1 && (
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {group.roles.length} roles
                      </span>
                    )}
                  </div>
                </div>

                {/* Roles within the company */}
                <div className="relative pl-5 sm:pl-6">
                  {/* connector line when multiple roles */}
                  {group.roles.length > 1 && (
                    <span className="absolute left-[5px] sm:left-[7px] top-2 bottom-2 w-px bg-primary-200 dark:bg-primary-800" />
                  )}
                  <ol className="space-y-5">
                    {group.roles.map((role, rIndex) => {
                      const durationText = formatDuration(role.duration)
                      return (
                        <li key={rIndex} className="relative">
                          {/* dot */}
                          <span className="absolute -left-5 sm:-left-6 top-1.5 w-2.5 h-2.5 rounded-full bg-primary-500 ring-4 ring-gray-50 dark:ring-gray-800" />

                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                              {role.position}
                            </h4>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${getTypeColor(role.type)}`}>
                              {role.type}
                            </span>
                          </div>

                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                              <Calendar className="h-3.5 w-3.5 mr-1.5" />
                              {role.duration}
                              {durationText && <span className="ml-1.5 text-gray-400 dark:text-gray-500">· {durationText}</span>}
                            </span>
                            <span className="flex items-center">
                              <MapPin className="h-3.5 w-3.5 mr-1.5" />
                              {role.location}
                            </span>
                          </div>

                          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            {role.description}
                          </p>

                          {role.skills && role.skills.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {role.skills.map((skill, sIndex) => (
                                <span
                                  key={sIndex}
                                  className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-100 dark:border-primary-800/50"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ol>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Summary Stats */}
        <div className="mt-16 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {experiences.length}
            </div>
            <div className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Positions Held</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {careerYears}+
            </div>
            <div className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Years Experience</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
              {companyCount}
            </div>
            <div className="text-xs sm:text-base text-gray-600 dark:text-gray-400">Companies</div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Experience
